import { css } from '@emotion/css';
import { Fragment, useEffect, useRef, useState } from 'react';

import { GrafanaTheme2 } from '@grafana/data';
import { Trans, t } from '@grafana/i18n';
import { Box, Divider, EmptyState, ScrollContainer, Stack, useStyles2 } from '@grafana/ui';
import { attachSkeleton, SkeletonComponent } from '@grafana/ui/unstable';
import { generatedAPI } from 'app/extensions/api/clients/querylibrary/v0alpha1/endpoints.gen';

import { OnSelectQueryType } from '../../../features/explore/QueryLibrary/types';
import { QueryTemplateRow } from '../types';

import { QueryLibraryActions } from './QueryLibraryActions';
import { QueryLibraryDetails } from './QueryLibraryDetails';
import { QueryLibraryItem } from './QueryLibraryItem';

export interface QueryLibraryContentProps {
  isFiltered?: boolean;
  onSelectQuery: OnSelectQueryType;
  queryRows: QueryTemplateRow[];
  userFavorites: { [key: string]: boolean };
  onFavorite: (uid: string) => void;
  onUnfavorite: (uid: string) => void;
  highlightedQuery?: string;
}

function QueryLibraryContentComponent({
  isFiltered,
  onSelectQuery,
  queryRows,
  userFavorites,
  onFavorite,
  onUnfavorite,
  highlightedQuery,
}: QueryLibraryContentProps) {
  const [isEditingQuery, setIsEditingQuery] = useState(false);
  const [selectedQueryRowIndex, setSelectedQueryRowIndex] = useState<number>(0);
  const queryItemsRef = useRef<HTMLDivElement>(null); // to handle auto-scrolling when highlightedQuery is set
  const [lastUpdatedUid, setLastUpdatedUid] = useState<string>();

  const isEmpty = queryRows.length === 0;
  const styles = useStyles2(getStyles);

  const { isSuccess, isFetching } = generatedAPI.endpoints.listQueryTemplate.useQueryState({});

  useEffect(() => {
    if (isSuccess && !isFetching) {
      if (highlightedQuery && queryRows.length > 0) {
        // Auto-select highlighted query when specified
        const highlightIndex = queryRows.findIndex((row) => row.uid === highlightedQuery);
        if (highlightIndex !== -1) {
          setSelectedQueryRowIndex(highlightIndex);
          setIsEditingQuery(false); // Ensure we're not in edit mode

          // Scroll to the highlighted query
          const highlightedElement = queryItemsRef.current?.querySelector(`[data-query-uid="${highlightedQuery}"]`);
          if (highlightedElement) {
            highlightedElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest',
            });
          }
        }
      } else {
        const newIndex = queryRows.findIndex((q) => q.uid === lastUpdatedUid);
        if (newIndex !== -1) {
          setSelectedQueryRowIndex(newIndex);
        } else {
          setSelectedQueryRowIndex((prevState) => (queryRows[prevState] ? prevState : prevState - 1));
        }
      }
    }
  }, [queryRows, isSuccess, isFetching, lastUpdatedUid, highlightedQuery]);

  const onEditQuerySuccess = (uid: string) => {
    setLastUpdatedUid(uid);
  };

  if (isEmpty) {
    // search miss
    return isFiltered ? (
      <EmptyState message={t('query-library.not-found.title', 'No results found')} variant="not-found">
        <Trans i18nKey="query-library.not-found.message">Try adjusting your search or filter criteria</Trans>
      </EmptyState>
    ) : (
      // true empty state
      <EmptyState
        message={t('query-library.empty-state.title', "You haven't saved any queries to the library yet")}
        variant="call-to-action"
      >
        <Trans i18nKey="query-library.empty-state.message">
          Start adding them from Explore or when editing a dashboard
        </Trans>
      </EmptyState>
    );
  }

  return (
    <Stack flex={1} gap={0} minHeight={0}>
      <Box display="flex" flex={1} minWidth={0}>
        <ScrollContainer>
          <Stack direction="column" gap={0} flex={1} minWidth={0} role="radiogroup" ref={queryItemsRef}>
            {queryRows.map((queryRow, index) => (
              <Fragment key={queryRow.uid}>
                <QueryLibraryItem
                  data-query-uid={queryRow.uid}
                  isSelected={queryRows[selectedQueryRowIndex]?.uid === queryRow.uid}
                  isFavorite={userFavorites?.[queryRow.uid ?? ''] ?? false}
                  onFavorite={() => onFavorite(queryRow.uid ?? '')}
                  onUnfavorite={() => onUnfavorite(queryRow.uid ?? '')}
                  isHighlighted={highlightedQuery === queryRow.uid}
                  onSelectQueryRow={() => {
                    setSelectedQueryRowIndex(index);
                    setIsEditingQuery(false);
                  }}
                  queryRow={queryRow}
                />
                <Divider spacing={0} />
              </Fragment>
            ))}
          </Stack>
        </ScrollContainer>
      </Box>
      <Divider direction="vertical" spacing={0} />
      <Box display="flex" flex={2} minWidth={0}>
        <ScrollContainer>
          <Box
            direction="column"
            display="flex"
            flex={1}
            paddingBottom={0}
            paddingLeft={2}
            paddingRight={1}
            paddingTop={2}
          >
            {queryRows[selectedQueryRowIndex] && (
              <>
                <Box flex={1}>
                  <QueryLibraryDetails
                    query={queryRows[selectedQueryRowIndex]}
                    editingQuery={isEditingQuery}
                    setEditingQuery={setIsEditingQuery}
                    onEditQuerySuccess={onEditQuerySuccess}
                  />
                </Box>
                <div className={styles.actions}>
                  <QueryLibraryActions
                    selectedQueryRow={queryRows[selectedQueryRowIndex]}
                    onSelectQuery={onSelectQuery}
                    isEditingQuery={isEditingQuery}
                    onEditQuerySuccess={onEditQuerySuccess}
                  />
                </div>
              </>
            )}
          </Box>
        </ScrollContainer>
      </Box>
    </Stack>
  );
}

const QueryLibraryContentSkeleton: SkeletonComponent = ({ rootProps }) => {
  return (
    <Stack flex={1} gap={0} minHeight={0} {...rootProps}>
      <Box display="flex" flex={1} minWidth={0}>
        <Stack direction="column" flex={1} gap={0} minWidth={0}>
          {new Array(5).fill(0).map((_, index) => (
            <Fragment key={index}>
              <QueryLibraryItem.Skeleton />
              <Divider spacing={0} />
            </Fragment>
          ))}
        </Stack>
      </Box>
      <Divider direction="vertical" spacing={0} />
      <Box display="flex" flex={2} minWidth={0}>
        <Box
          direction="column"
          display="flex"
          flex={1}
          paddingBottom={0}
          paddingLeft={2}
          paddingRight={1}
          paddingTop={2}
        >
          <QueryLibraryDetails.Skeleton />
        </Box>
      </Box>
    </Stack>
  );
};

export const QueryLibraryContent = attachSkeleton(QueryLibraryContentComponent, QueryLibraryContentSkeleton);

const getStyles = (theme: GrafanaTheme2) => ({
  actions: css({
    background: theme.colors.background.primary,
    bottom: 0,
    padding: theme.spacing(1, 0),
    position: 'sticky',
    zIndex: 1,
  }),
});
